import { Injectable } from '@angular/core';
import { debounceTime, filter, first, switchMap, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ProductDetailViewModel } from '@product-detail/models/product-detail-view-model';
import { productDetailsViewModelSelector } from '@product-detail/selectors/product-detail.selector';
import { Store } from '@ngrx/store';
import { ProductService } from '@shared/services/product/product.service';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { QuantityUpdate } from '@shared/models/quantity-update';
import { PlatformService, Tracking } from '@panamax/app-state';
import { PRODUCT_COMPARE } from '@search/shared/constants/search-config';
import { EllipsisClickData } from '@shared/models/usf-product-card.model';
import { TranslateService } from '@ngx-translate/core';
import { ActionSheetController } from '@ionic/angular';
import { ProductCompareDownloadService } from './product-compare-download.service';
import { ProductComparePrintHandlerService } from './product-compare-print-handler.service';
import { ProductCompareShareHandlerService } from './product-compare-share-handler.service';

@Injectable({
  providedIn: 'root',
})
export class ProductComparisonService {
  constructor(
    private store: Store,
    private productService: ProductService,
    private analyticsService: ProductAnalyticsService,
    private actionSheetController: ActionSheetController,
    private productComparePrintHandlerService: ProductComparePrintHandlerService,
    private productCompareShareHandlerService: ProductCompareShareHandlerService,
    private productCompareDownloadService: ProductCompareDownloadService,
    public translateService: TranslateService,
    private platformService: PlatformService,
  ) {}

  private loadProductDetails$(productNumbers: number[]) {
    return this.productService.loadProducts$(productNumbers).pipe(
      first(dispatched => dispatched),
      tap(_ => {
        this.productService.loadProductDetails(productNumbers);
      }),
    );
  }

  getProductDetailViewModels(
    productNumbers: number[],
    isDebounce = true,
  ): Observable<Map<number, ProductDetailViewModel>> {
    return this.store
      .select(productDetailsViewModelSelector(productNumbers))
      .pipe(
        filter(productDetails => !!productDetails),
        debounceTime(isDebounce ? 500 : 0),
      );
  }

  getProductDetails(
    productNumbers: number[],
    isDebounce = true,
  ): Observable<Map<number, ProductDetailViewModel>> {
    return this.loadProductDetails$(productNumbers).pipe(
      switchMap(() =>
        this.getProductDetailViewModels(productNumbers, isDebounce),
      ),
    );
  }

  trackProductComparePageLoad(products: ProductDetailViewModel[]) {
    const productsData: any[] = [];
    if (!!products) {
      products.forEach(product => {
        if (product && !!product.summary) {
          productsData.push({
            divisionApn:
              product.summary.divisionNumber +
              '-' +
              product.summary.productNumber,
            attributes: product.trackingAttributes,
          });
        }
      });

      if (productsData.length > 0) {
        this.analyticsService.trackProductComparePageLoad(productsData);
      }
    }
  }

  trackProductCompareEvent(products: ProductDetailViewModel[]) {
    const productData = {
      attributes: products[0].trackingAttributes,
      divisionNumber: products[0].summary.divisionNumber,
      productNumber: products[0].productNumber,
      betterBuy: {
        attributes: products[0].betterBuy?.product.trackingAttributes,
        divisionNumber: products[0].betterBuy?.product.summary.divisionNumber,
        productNumber: products[0].betterBuy?.product.productNumber,
      },
    };
    this.analyticsService.trackProductCompare(productData);
  }

  extractChangeQuantityTrackingData(
    quantityUpdate: QuantityUpdate,
    productsDetails: ProductDetailViewModel[],
  ): Tracking {
    let notOrderedProductArray = productsDetails
      .filter(
        productDetail =>
          productDetail?.productNumber !== quantityUpdate?.productNumber,
      )
      .map(productDetail => ({
        divisionApn: `${productDetail?.summary?.divisionNumber}-${productDetail?.productNumber}`,
        attributes: productDetail?.trackingAttributes,
        compareProductNotOrdered: '1',
      }));

    return {
      analytics: {
        data: {
          products: [{ addMethod: PRODUCT_COMPARE }, ...notOrderedProductArray],
        },
      },
    };
  }

  constructEllipsisButtons(
    ellipsisClickData: EllipsisClickData,
    productDetails: ProductDetailViewModel[],
    isPrintButtonEnabled: boolean,
    isDownloadButtonEnabled: boolean,
    isShareButtonEnabled: boolean,
  ) {
    let buttons = [];
    isShareButtonEnabled &&
      buttons.push(this.getShareBtn(ellipsisClickData, productDetails));
    isPrintButtonEnabled &&
      buttons.push(this.getPrintBtn(ellipsisClickData, productDetails));
    isDownloadButtonEnabled &&
      buttons.push(this.getDownloadBtn(ellipsisClickData, productDetails));

    return buttons;
  }
  async openProductCompareEllipsis(buttons: any[]) {
    buttons.push({
      text: this.translateService.instant('i18n.common.cancel'),
      role: 'cancel',
      cssClass: 'cancel-button',
    });
    const actionSheet = await this.actionSheetController.create({
      mode: 'ios',
      buttons,
      cssClass: 'action-sheet-touch',
    });
    await actionSheet.present();
  }

  getShareBtn(
    ellipsisClickData: EllipsisClickData,
    productDetails: ProductDetailViewModel[],
  ) {
    return {
      text: this.translateService.instant('i18n.common.share'),
      handler: () => {
        this.shareProductCompare(productDetails);
      },
    };
  }
  getPrintBtn(
    ellipsisClickData: EllipsisClickData,
    productDetails: ProductDetailViewModel[],
  ) {
    return {
      text: this.translateService.instant('i18n.common.print'),
      handler: () => {
        this.printProductCompare(productDetails);
      },
    };
  }
  getDownloadBtn(
    ellipsisClickData: EllipsisClickData,
    productDetails: ProductDetailViewModel[],
  ) {
    return {
      text: this.translateService.instant('i18n.common.download'),
      handler: () => {
        this.downloadProductCompare(productDetails);
      },
    };
  }

  printProductCompare(productDetails: ProductDetailViewModel[]) {
    this.productComparePrintHandlerService.showPdfPrompt(productDetails);
  }

  shareProductCompare(productDetails: ProductDetailViewModel[]) {
    if (
      this.platformService.platformType !==
      this.platformService.platformEnum.desktop
    ) {
      this.productCompareShareHandlerService.showPdfPrompt(productDetails);
    } else {
      this.productCompareShareHandlerService.showShareViaEmailModal(
        productDetails,
      );
    }
  }

  downloadProductCompare(productDetails: ProductDetailViewModel[]) {
    this.productCompareDownloadService.showDownloadModal(productDetails);
  }
}
